import { createLogger } from '@/lib/logger';

const logger = createLogger('utils/mobile-auth');

/**
 * Mobile Authentication Utilities
 * Handles mobile app authentication flow via web login
 */

const MOBILE_PLATFORM_KEY = 'mobile_auth_platform';
const MOBILE_REDIRECT_KEY = 'mobile_auth_redirect';
const ALLOWED_MOBILE_SCHEMES = new Set(['scrambl', 'scrambl-dev', 'scrambl-local']);
const DEFAULT_MOBILE_REDIRECT_URI =
  (import.meta.env.VITE_MOBILE_REDIRECT_URI as string | undefined) || 'scrambl://auth/callback';

export interface MobileAuthContext {
  platform: 'mobile';
  redirect: string;
}

function isAllowedMobileRedirect(redirect: string): boolean {
  try {
    const parsed = new URL(redirect);
    const scheme = parsed.protocol.replace(':', '').toLowerCase();
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname;

    if (!ALLOWED_MOBILE_SCHEMES.has(scheme)) {
      return false;
    }

    // Accept:
    // - scrambl://auth/callback  -> host=auth, path=/callback
    // - scrambl:///auth/callback -> host="", path=/auth/callback
    const isExpectedCallbackPath =
      (host === 'auth' && path === '/callback') ||
      (host === '' && path === '/auth/callback');

    return isExpectedCallbackPath;
  } catch {
    return false;
  }
}

/**
 * Check if current request is from mobile app
 */
export function isMobileAuthFlow(): boolean {
  return sessionStorage.getItem(MOBILE_PLATFORM_KEY) === 'mobile';
}

/**
 * Get stored mobile auth context
 */
export function getMobileAuthContext(): MobileAuthContext | null {
  const platform = sessionStorage.getItem(MOBILE_PLATFORM_KEY);
  const redirect = sessionStorage.getItem(MOBILE_REDIRECT_KEY);

  if (platform === 'mobile' && redirect) {
    return { platform: 'mobile', redirect };
  }

  return null;
}

/**
 * Store mobile auth context from URL params
 */
export function storeMobileAuthContext(platform: string | null, redirect: string | null): boolean {
  if (platform === 'mobile' && redirect) {
    if (!isAllowedMobileRedirect(redirect)) {
      logger.warn('[MobileAuth] Ignoring untrusted mobile redirect URI', { redirect });
      clearMobileAuthContext();
      return false;
    }

    sessionStorage.setItem(MOBILE_PLATFORM_KEY, 'mobile');
    sessionStorage.setItem(MOBILE_REDIRECT_KEY, redirect);
    return true;
  }
  return false;
}

/**
 * Clear mobile auth context
 */
export function clearMobileAuthContext(): void {
  sessionStorage.removeItem(MOBILE_PLATFORM_KEY);
  sessionStorage.removeItem(MOBILE_REDIRECT_KEY);
}

/**
 * Build mobile redirect URL with tokens
 */
export function buildMobileRedirectUrl(
  tokens: { accessToken: string; refreshToken?: string; expiresIn?: number }
): string | null {
  const context = getMobileAuthContext();
  const baseUrl = context?.redirect || DEFAULT_MOBILE_REDIRECT_URI;

  try {
    // Build URL manually since custom schemes (scrambl://) may not work well with URL constructor.
    // Use query parameters for compatibility with native auth sessions that can drop URL fragments.
    if (!context) {
      logger.warn('[MobileAuth] Missing mobile auth context; using default redirect URI', { baseUrl });
    }

    if (!isAllowedMobileRedirect(baseUrl)) {
      logger.warn('[MobileAuth] Refusing to build redirect for untrusted URI', { baseUrl });
      clearMobileAuthContext();
      return null;
    }

    const params = new URLSearchParams();

    params.set('accessToken', tokens.accessToken);

    if (tokens.refreshToken) {
      params.set('refreshToken', tokens.refreshToken);
    }

    if (tokens.expiresIn) {
      params.set('expiresIn', String(tokens.expiresIn));
    }

    const serializedParams = params.toString();
    const separator = baseUrl.includes('?') ? '&' : '?';
    const finalUrl = `${baseUrl}${separator}${serializedParams}`;

    // Clear context after building URL
    clearMobileAuthContext();
    return finalUrl;
  } catch (error) {
    logger.error('[MobileAuth] Failed to build redirect URL:', error);
    clearMobileAuthContext();
    return null;
  }
}

/**
 * Redirect to mobile app with tokens
 * Returns true if redirect was performed, false otherwise
 */
export function redirectToMobileApp(
  tokens: { accessToken: string; refreshToken?: string; expiresIn?: number }
): boolean {
  const redirectUrl = buildMobileRedirectUrl(tokens);

  if (redirectUrl) {
    window.location.href = redirectUrl;
    return true;
  }

  return false;
}
